import { useRef } from 'react';

// =============================================================================
// useSubmitGuard — synchronous double-submit guard for mutation triggers
// =============================================================================
// react-query's `isPending` (and therefore a <Button>'s `loading`/`disabled`)
// only flips true AFTER a re-render. Two taps fired before React repaints — a
// common "ghost double-tap" on POS tablets — both see an enabled button and fire
// the mutation twice, creating duplicate orders / quotations / customers.
//
// The server-side idempotency middleware can't catch this: the axios interceptor
// mints a FRESH `Idempotency-Key` per call, so two taps carry two different keys
// and both go through. This ref-based lock is what actually stops the second tap
// — it blocks re-entry in the same tick, before any state update lands.
//
// Usage:
//   const guard = useSubmitGuard();
//   const create = useMutation({ mutationFn, onSuccess, onSettled: guard.release });
//   <Button loading={create.isPending} onClick={() => guard.run(() => create.mutate())} />
//
// `run` no-ops until the mutation settles (success OR error), so a failed submit
// still lets the user retry. One guard can be shared by several mutually-exclusive
// mutations on a page (e.g. "Save draft" / "Confirm") — the first to fire locks
// out the rest until it settles.
export interface SubmitGuard {
  /** Run `fn` only if no guarded submit is currently in flight. */
  run: (fn: () => void) => void;
  /** Release the lock — pass as the mutation's `onSettled`. */
  release: () => void;
}

export function useSubmitGuard(): SubmitGuard {
  const locked = useRef(false);
  return {
    run: (fn: () => void): void => {
      if (locked.current) return;
      locked.current = true;
      fn();
    },
    release: (): void => {
      locked.current = false;
    },
  };
}
